import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import DOMPurify from 'dompurify';
import { 
  FileText, Plus, Edit, Trash2, Eye, Save, X, Package, Truck, 
  Briefcase, ShoppingCart, Home, Heart, HelpCircle, Printer, Download,
  CheckCircle, AlertCircle, GripVertical, ClipboardCheck,
  FileSignature, UserCheck, Star, BookOpen,
  PenTool, Send, Clock, CheckCircle2, XCircle, List, Inbox
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import api from '../../services/api';
import SignaturePad from '../ui/SignaturePad';

// Sanitize HTML helper for print content
const sanitizeHtml = (html) => DOMPurify.sanitize(html);

// Safe HTML escaping to prevent XSS
const escapeHtml = (text) => {
  if (!text) return '';
  const div = document.createElement('div');
  div.textContent = String(text);
  return div.innerHTML;
};

const iconMap = {
  'package': Package, 'truck': Truck, 'briefcase': Briefcase,
  'shopping-cart': ShoppingCart, 'home': Home, 'heart': Heart,
  'help-circle': HelpCircle, 'file-text': FileText, 'edit': Edit,
  'clipboard-check': ClipboardCheck, 'file-signature': FileSignature,
  'user-check': UserCheck, 'star': Star, 'book-open': BookOpen
};

const formCategories = [
  { id: 'service_requests', name: 'Service Requests', icon: 'file-text', color: 'blue' },
  { id: 'job_completion', name: 'Job Completion', icon: 'clipboard-check', color: 'green' },
  { id: 'waivers', name: 'Waivers', icon: 'file-signature', color: 'purple' },
  { id: 'evaluations', name: 'Evaluations', icon: 'star', color: 'amber' },
  { id: 'onboarding', name: 'Onboarding', icon: 'user-check', color: 'indigo' }
];

// Built-in forms
const builtInForms = {
  service_requests: [
    { id: 'local_pickup', name: 'Local Pickup Request', description: 'Request pickup of items from a store or business', icon: 'package', 
      fields: [
        { name: 'Customer Name', type: 'text', required: true },
        { name: 'Phone Number', type: 'phone', required: true },
        { name: 'Pickup Address', type: 'address', required: true },
        { name: 'Store/Business Name', type: 'text', required: true },
        { name: 'Item Description', type: 'textarea', required: true },
        { name: 'Estimated Item Weight', type: 'select', required: false, options: ['Under 10 lbs', '10-25 lbs', '25-50 lbs', 'Over 50 lbs'] },
        { name: 'Delivery Address', type: 'address', required: true },
        { name: 'Is Item Prepaid?', type: 'checkbox', required: true },
        { name: 'Special Instructions', type: 'textarea', required: false },
        { name: 'Preferred Pickup Time', type: 'datetime', required: true },
        { name: 'Customer Signature', type: 'signature', required: true }
      ]
    },
    { id: 'home_support', name: 'Errands & Tasks Request', description: 'General assistance and tasks', icon: 'home',
      fields: [
        { name: 'Client Name', type: 'text', required: true },
        { name: 'Phone Number', type: 'phone', required: true },
        { name: 'Service Category', type: 'select', required: true, options: ['Grocery Shopping', 'Prescription Pickup', 'Store Runs', 'General Errands', 'Light Tasks'] },
        { name: 'Service Address', type: 'address', required: true },
        { name: 'Tasks Requested', type: 'textarea', required: true },
        { name: 'Estimated Duration (hours)', type: 'number', required: true },
        { name: 'Preferred Date', type: 'date', required: true },
        { name: 'Preferred Time', type: 'time', required: true },
        { name: 'Special Instructions', type: 'textarea', required: false },
        { name: 'Client Signature', type: 'signature', required: true }
      ]
    }
  ],
  job_completion: [
    { id: 'service_completion', name: 'Service Completion', description: 'Complete after any service job', icon: 'home',
      fields: [
        { name: 'Client Name', type: 'text', required: true },
        { name: 'Service Date', type: 'date', required: true },
        { name: 'Arrival Time', type: 'time', required: true },
        { name: 'Departure Time', type: 'time', required: true },
        { name: 'Service Type', type: 'select', required: true, options: ['Rides', 'Laundry', 'Courier', 'Errands'] },
        { name: 'Tasks Completed', type: 'textarea', required: true },
        { name: 'Any Issues Noted', type: 'textarea', required: false },
        { name: 'Worker Signature', type: 'signature', required: true },
        { name: 'Client Signature', type: 'signature', required: false }
      ]
    },
    { id: 'courier_completion', name: 'Courier Delivery Completion', description: 'Complete after courier deliveries', icon: 'truck',
      fields: [
        { name: 'Pickup Location', type: 'address', required: true },
        { name: 'Pickup Time', type: 'datetime', required: true },
        { name: 'Picked Up From (Name)', type: 'text', required: true },
        { name: 'Delivery Location', type: 'address', required: true },
        { name: 'Delivery Time', type: 'datetime', required: true },
        { name: 'Delivered To (Name)', type: 'text', required: true },
        { name: 'Package Condition', type: 'select', required: true, options: ['Excellent', 'Good', 'Damaged - Noted'] },
        { name: 'Notes', type: 'textarea', required: false },
        { name: 'Recipient Signature', type: 'signature', required: true },
        { name: 'Worker Signature', type: 'signature', required: true }
      ]
    }
  ],
  waivers: [
    { id: 'liability_waiver', name: 'General Liability Waiver', description: 'Standard liability release', icon: 'file-signature', required: true,
      fields: [
        { name: 'Full Legal Name', type: 'text', required: true },
        { name: 'Date', type: 'date', required: true },
        { name: 'Address', type: 'address', required: true },
        { name: 'Phone', type: 'phone', required: true },
        { name: 'Email', type: 'email', required: true },
        { name: 'I acknowledge that I have read and understand the terms of service', type: 'checkbox', required: true },
        { name: 'I release NC Help Hub from liability for any damages not caused by negligence', type: 'checkbox', required: true },
        { name: 'I understand that services provided are non-medical in nature', type: 'checkbox', required: true },
        { name: 'Signature', type: 'signature', required: true },
        { name: 'Date Signed', type: 'date', required: true }
      ]
    }
  ],
  evaluations: [
    { id: 'performance_review', name: 'Worker Performance Review', description: 'Regular performance evaluation', icon: 'star',
      fields: [
        { name: 'Employee Name', type: 'text', required: true },
        { name: 'Employee ID', type: 'text', required: true },
        { name: 'Review Period Start', type: 'date', required: true },
        { name: 'Review Period End', type: 'date', required: true },
        { name: 'Attendance Rating (1-5)', type: 'rating', required: true },
        { name: 'Quality of Work Rating (1-5)', type: 'rating', required: true },
        { name: 'Communication Rating (1-5)', type: 'rating', required: true },
        { name: 'Professionalism Rating (1-5)', type: 'rating', required: true },
        { name: 'Strengths', type: 'textarea', required: true },
        { name: 'Areas for Improvement', type: 'textarea', required: true },
        { name: 'Goals for Next Period', type: 'textarea', required: true },
        { name: 'Overall Rating (1-5)', type: 'rating', required: true },
        { name: 'Supervisor Signature', type: 'signature', required: true },
        { name: 'Employee Signature', type: 'signature', required: true },
        { name: 'Date', type: 'date', required: true }
      ]
    }
  ],
  onboarding: [
    { id: 'personal_info', name: 'Employee Information Form', description: 'Basic employee information', icon: 'user-check',
      fields: [
        { name: 'Full Legal Name', type: 'text', required: true },
        { name: 'Preferred Name', type: 'text', required: false },
        { name: 'Date of Birth', type: 'date', required: true },
        { name: 'Social Security Number (Last 4)', type: 'text', required: true },
        { name: 'Street Address', type: 'text', required: true },
        { name: 'City', type: 'text', required: true },
        { name: 'State', type: 'text', required: true },
        { name: 'Zip Code', type: 'text', required: true },
        { name: 'Phone Number', type: 'phone', required: true },
        { name: 'Email Address', type: 'email', required: true },
        { name: 'Emergency Contact Name', type: 'text', required: true },
        { name: 'Emergency Contact Relationship', type: 'text', required: true },
        { name: 'Emergency Contact Phone', type: 'phone', required: true },
        { name: 'Signature', type: 'signature', required: true },
        { name: 'Date', type: 'date', required: true }
      ]
    },
    { id: 'direct_deposit', name: 'Direct Deposit Authorization', description: 'Banking information for payroll', icon: 'file-text',
      fields: [
        { name: 'Employee Name', type: 'text', required: true },
        { name: 'Bank Name', type: 'text', required: true },
        { name: 'Routing Number', type: 'text', required: true },
        { name: 'Account Number', type: 'text', required: true },
        { name: 'Account Type', type: 'select', required: true, options: ['Checking', 'Savings'] },
        { name: 'I authorize NC Help Hub to deposit my pay electronically', type: 'checkbox', required: true },
        { name: 'Signature', type: 'signature', required: true },
        { name: 'Date', type: 'date', required: true }
      ]
    }
  ]
};

const FormTemplatesTab = () => {
  const [mainTab, setMainTab] = useState('templates'); // 'templates' or 'submissions'
  const [activeCategory, setActiveCategory] = useState('service_requests');
  const [customTemplates, setCustomTemplates] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewModal, setViewModal] = useState(null);
  const [fillModal, setFillModal] = useState(null);
  const [viewSubmissionModal, setViewSubmissionModal] = useState(null);
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const printRef = useRef(null);
  
  const [formValues, setFormValues] = useState({});
  const [signatures, setSignatures] = useState({});
  
  const [formData, setFormData] = useState({
    name: '', description: '', category: 'service_requests', icon: 'file-text',
    fields: [], is_active: true
  });
  const [newField, setNewField] = useState({ name: '', type: 'text', required: true });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [templatesRes, submissionsRes] = await Promise.all([
        api.get('/forms/custom-templates').catch(() => ({ data: [] })),
        api.get('/forms/signed-submissions').catch(() => ({ data: { submissions: [] } }))
      ]);
      setCustomTemplates(templatesRes.data || []);
      setSubmissions(submissionsRes.data?.submissions || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // Download submission as PDF
  const handleDownloadSubmissionPDF = async (submission) => {
    try {
      const response = await api.get(`/forms/signed-submissions/${submission.id}/pdf`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `form_submission_${submission.id.slice(0, 8).toUpperCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('Form downloaded as PDF');
    } catch (error) {
      console.error('Failed to download PDF:', error);
      toast.error('Failed to download form as PDF');
    }
  };

  const handlePrint = () => {
    const printContent = generatePrintableForm(viewModal || fillModal);
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (printWindow) {
      const doc = printWindow.document;
      const html = doc.createElement('html');
      const head = doc.createElement('head');
      const body = doc.createElement('body');
      const container = doc.createElement('div');
      container.innerHTML = sanitizeHtml(printContent);
      body.appendChild(container);
      html.appendChild(head);
      html.appendChild(body);
      doc.documentElement.replaceWith(html);
      setTimeout(() => { printWindow.focus(); printWindow.print(); }, 300);
    }
  };

  const generatePrintableForm = (form) => {
    // Escape all form data to prevent XSS
    const safeName = escapeHtml(form.name);
    const safeDescription = escapeHtml(form.description);
    const safeId = escapeHtml(form.id?.toUpperCase() || 'CC-FORM');
    
    const fieldsHtml = (form.fields || []).map((field) => {
      const fieldName = escapeHtml(typeof field === 'string' ? field.replace(/_/g, ' ') : field.name);
      const isRequired = typeof field === 'object' ? field.required : false;
      const fieldType = typeof field === 'object' ? field.type : 'text';
      
      if (fieldType === 'signature') {
        return `<div class="signature-box"><div class="signature-line"></div><p class="signature-label">${fieldName}${isRequired ? ' *' : ''}</p><p class="date-line">Date: _______________</p></div>`;
      }
      if (fieldType === 'checkbox') {
        return `<div class="checkbox-row"><span class="checkbox-box"></span><span class="checkbox-label">${fieldName}${isRequired ? ' *' : ''}</span></div>`;
      }
      if (fieldType === 'textarea') {
        return `<div class="field-row"><label>${fieldName}${isRequired ? ' *' : ''}</label><div class="textarea-box"></div></div>`;
      }
      if (fieldType === 'rating') {
        return `<div class="field-row"><label>${fieldName}${isRequired ? ' *' : ''}</label><div class="rating-boxes"><span class="rating-box">1</span><span class="rating-box">2</span><span class="rating-box">3</span><span class="rating-box">4</span><span class="rating-box">5</span></div></div>`;
      }
      if (fieldType === 'select' && field.options) {
        const opts = field.options.map(o => `<span class="select-option"><span class="checkbox-box small"></span> ${escapeHtml(o)}</span>`).join('');
        return `<div class="field-row"><label>${fieldName}${isRequired ? ' *' : ''}</label><div class="select-options">${opts}</div></div>`;
      }
      return `<div class="field-row"><label>${fieldName}${isRequired ? ' *' : ''}</label><div class="input-line"></div></div>`;
    }).join('');

    return `<!DOCTYPE html><html><head><title>${safeName} - NC Help Hub</title><style>
      @page{margin:0.75in}*{box-sizing:border-box;margin:0;padding:0}body{font-family:'Times New Roman',serif;font-size:11pt;line-height:1.4;color:#000;background:#fff}
      .form-container{max-width:8.5in;margin:0 auto;padding:0.5in}.form-header{text-align:center;border-bottom:3px double #1a365d;padding-bottom:15px;margin-bottom:20px}
      .company-name{font-size:24pt;font-weight:bold;color:#1a365d;letter-spacing:2px}.company-tagline{font-size:9pt;color:#666;margin-top:3px}
      .company-contact{font-size:8pt;color:#444;margin-top:8px}.form-title{font-size:16pt;font-weight:bold;text-align:center;margin:20px 0 10px;padding:10px;background:#f0f4f8;border:1px solid #1a365d}
      .form-subtitle{text-align:center;font-size:10pt;color:#666;margin-bottom:25px;font-style:italic}.form-id{text-align:right;font-size:8pt;color:#999;margin-bottom:15px}
      .field-row{margin-bottom:18px}.field-row label{display:block;font-weight:bold;font-size:10pt;margin-bottom:5px;color:#333}.input-line{border-bottom:1px solid #000;height:25px;width:100%}
      .textarea-box{border:1px solid #000;height:80px;width:100%}.checkbox-row{display:flex;align-items:flex-start;margin-bottom:12px;gap:10px}
      .checkbox-box{width:14px;height:14px;border:1px solid #000;display:inline-block;flex-shrink:0;margin-top:2px}.checkbox-box.small{width:10px;height:10px}
      .checkbox-label{font-size:10pt;line-height:1.3}.select-options{display:flex;flex-wrap:wrap;gap:15px;margin-top:5px}.select-option{display:flex;align-items:center;gap:5px;font-size:9pt}
      .rating-boxes{display:flex;gap:10px;margin-top:5px}.rating-box{width:30px;height:30px;border:1px solid #000;display:flex;align-items:center;justify-content:center;font-weight:bold}
      .signature-box{margin-top:30px;margin-bottom:20px}.signature-line{border-bottom:1px solid #000;height:50px;width:60%;margin-bottom:5px}.signature-label{font-size:9pt;font-weight:bold}
      .date-line{font-size:9pt;margin-top:10px}.form-footer{margin-top:40px;padding-top:15px;border-top:1px solid #ccc;font-size:8pt;color:#666;text-align:center}
      .required-note{font-size:8pt;color:#666;margin-bottom:20px}@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
    </style></head><body><div class="form-container">
      <div class="form-header"><div class="company-name">NC HELP HUB</div><div class="company-tagline">Rides, Courier, Laundry & Errands Services</div><div class="company-contact">Tarboro, NC 27886 | nchelphub@outlook.com</div></div>
      <div class="form-id">Form ID: ${safeId} | Rev. 2025</div>
      <div class="form-title">${safeName}</div><div class="form-subtitle">${safeDescription}</div>
      <p class="required-note">Fields marked with * are required</p>${fieldsHtml}
      <div class="form-footer"><p>NC Help Hub | Non-Medical Services Only</p><p>This form is the property of NC Help Hub.</p></div>
    </div></body></html>`;
  };

  const handleSubmitForm = async () => {
    if (!fillModal) return;
    
    // Validate required fields
    const missingFields = [];
    (fillModal.fields || []).forEach(field => {
      const fieldName = typeof field === 'string' ? field : field.name;
      const isRequired = typeof field === 'object' ? field.required : false;
      const fieldType = typeof field === 'object' ? field.type : 'text';
      
      if (isRequired) {
        if (fieldType === 'signature') {
          if (!signatures[fieldName]) missingFields.push(fieldName);
        } else {
          if (!formValues[fieldName]) missingFields.push(fieldName);
        }
      }
    });
    
    if (missingFields.length > 0) {
      toast.error(`Please complete required fields: ${missingFields.slice(0, 3).join(', ')}${missingFields.length > 3 ? '...' : ''}`);
      return;
    }
    
    setSaving(true);
    try {
      const submissionData = {
        template_id: fillModal.id,
        template_name: fillModal.name,
        category: activeCategory,
        ...formValues,
        ...Object.fromEntries(Object.entries(signatures).map(([k, v]) => [`${k}_signature`, v]))
      };
      
      await api.post('/forms/signed-submissions', submissionData);
      toast.success('Form submitted successfully!');
      setFillModal(null);
      setFormValues({});
      setSignatures({});
      fetchData();
    } catch (error) {
      toast.error('Failed to submit form');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateTemplate = async () => {
    if (!formData.name.trim()) { toast.error('Template name required'); return; }
    if (formData.fields.length === 0) { toast.error('Add at least one field'); return; }
    setSaving(true);
    try {
      await api.post('/forms/custom-templates', formData);
      toast.success('Template created');
      setCreateModal(false);
      setFormData({ name: '', description: '', category: activeCategory, icon: 'file-text', fields: [], is_active: true });
      fetchData();
    } catch (error) {
      toast.error('Failed to create template');
    } finally { setSaving(false); }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await api.delete(`/forms/custom-templates/${templateId}`);
      toast.success('Template deleted');
      fetchData();
    } catch (error) { toast.error('Failed to delete'); }
  };

  // Delete form submission
  const handleDeleteSubmission = async (submissionId) => {
    if (!window.confirm('Delete this form submission? This cannot be undone.')) return;
    try {
      await api.delete(`/forms/submissions/${submissionId}`);
      toast.success('Submission deleted');
      fetchData();
    } catch (error) { 
      toast.error('Failed to delete submission'); 
    }
  };

  const addField = () => {
    if (!newField.name.trim()) { toast.error('Field name required'); return; }
    setFormData(prev => ({ ...prev, fields: [...prev.fields, { ...newField, id: Date.now() }] }));
    setNewField({ name: '', type: 'text', required: true });
  };

  const removeField = (fieldId) => {
    setFormData(prev => ({ ...prev, fields: prev.fields.filter(f => f.id !== fieldId) }));
  };

  const getIconComponent = (iconName) => iconMap[iconName] || FileText;

  // Render fillable field
  const renderFillableField = (field, idx) => {
    const fieldName = typeof field === 'string' ? field.replace(/_/g, ' ') : field.name;
    const isRequired = typeof field === 'object' ? field.required : false;
    const fieldType = typeof field === 'object' ? field.type : 'text';

    if (fieldType === 'signature') {
      return (
        <div key={idx} className="mb-6 p-4 bg-[var(--background-subtle)] rounded-lg border">
          <SignaturePad 
            label={`${fieldName}${isRequired ? ' *' : ''}`}
            onSave={(sig) => setSignatures(prev => ({ ...prev, [fieldName]: sig }))}
            width={350}
            height={120}
          />
          {signatures[fieldName] && (
            <p className="text-sm text-[var(--success)] mt-2 flex items-center gap-1">
              <CheckCircle2 className="w-4 h-4" /> Signature captured
            </p>
          )}
        </div>
      );
    }

    if (fieldType === 'checkbox') {
      return (
        <div key={idx} className="flex items-start gap-3 mb-4">
          <input 
            type="checkbox" 
            checked={formValues[fieldName] || false}
            onChange={(e) => setFormValues(prev => ({ ...prev, [fieldName]: e.target.checked }))}
            className="mt-1 w-5 h-5 rounded border-[var(--border-primary)]"
          />
          <label className="text-sm text-[var(--text-primary)]">{fieldName}{isRequired && <span className="text-[var(--error)]"> *</span>}</label>
        </div>
      );
    }

    if (fieldType === 'textarea') {
      return (
        <div key={idx} className="mb-4">
          <label className="block text-sm font-medium text-[var(--text-primary)] mb-1">{fieldName}{isRequired && <span className="text-[var(--error)]"> *</span>}</label>
          <Textarea 
            value={formValues[fieldName] || ''}
            onChange={(e) => setFormValues(prev => ({ ...prev, [fieldName]: e.target.value }))}
            rows={3}
            className="w-full"
          />
        </div>
      );
    }

    if (fieldType === 'select' && field.options) {
      return (
        <div key={idx} className="mb-4">
          <label className="block text-sm font-medium text-[var(--text-primary)] mb-1">{fieldName}{isRequired && <span className="text-[var(--error)]"> *</span>}</label>
          <select 
            value={formValues[fieldName] || ''}
            onChange={(e) => setFormValues(prev => ({ ...prev, [fieldName]: e.target.value }))}
            className="w-full px-3 py-2 border border-[var(--border-primary)] rounded-lg bg-[var(--background-subtle)] text-[var(--text-primary)]"
          >
            <option value="">Select...</option>
            {field.options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </div>
      );
    }

    if (fieldType === 'rating') {
      return (
        <div key={idx} className="mb-4">
          <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">{fieldName}{isRequired && <span className="text-[var(--error)]"> *</span>}</label>
          <div className="flex gap-2">
            {[1,2,3,4,5].map(n => (
              <button key={n} type="button"
                onClick={() => setFormValues(prev => ({ ...prev, [fieldName]: n }))}
                className={`w-10 h-10 rounded-lg border-2 font-bold ${formValues[fieldName] === n ? 'bg-[var(--primary)] text-white border-[var(--primary)]' : 'border-[var(--border-primary)] hover:border-[var(--primary)]'}`}
              >{n}</button>
            ))}
          </div>
        </div>
      );
    }

    const inputType = fieldType === 'email' ? 'email' : fieldType === 'phone' ? 'tel' : fieldType === 'date' ? 'date' : fieldType === 'time' ? 'time' : fieldType === 'datetime' ? 'datetime-local' : fieldType === 'number' ? 'number' : 'text';

    return (
      <div key={idx} className="mb-4">
        <label className="block text-sm font-medium text-[var(--text-primary)] mb-1">{fieldName}{isRequired && <span className="text-[var(--error)]"> *</span>}</label>
        <Input 
          type={inputType}
          value={formValues[fieldName] || ''}
          onChange={(e) => setFormValues(prev => ({ ...prev, [fieldName]: e.target.value }))}
          className="w-full"
        />
      </div>
    );
  };

  // Render print preview field
  const renderPreviewField = (field, idx) => {
    const fieldName = typeof field === 'string' ? field.replace(/_/g, ' ') : field.name;
    const isRequired = typeof field === 'object' ? field.required : false;
    const fieldType = typeof field === 'object' ? field.type : 'text';

    if (fieldType === 'signature') {
      return (
        <div key={idx} className="mb-6">
          <div className="border-b-2 border-gray-400 h-16 mb-1"></div>
          <p className="text-sm font-semibold text-[var(--text-primary)]">{fieldName}{isRequired && <span className="text-[var(--error)]"> *</span>}</p>
          <p className="text-xs text-[var(--text-muted)] mt-1">Date: _______________</p>
        </div>
      );
    }
    if (fieldType === 'checkbox') {
      return (
        <div key={idx} className="flex items-start gap-3 mb-3">
          <div className="w-5 h-5 border-2 border-gray-400 rounded mt-0.5"></div>
          <span className="text-sm text-[var(--text-primary)]">{fieldName}{isRequired && <span className="text-[var(--error)]"> *</span>}</span>
        </div>
      );
    }
    if (fieldType === 'textarea') {
      return (
        <div key={idx} className="mb-4">
          <label className="block text-sm font-semibold text-[var(--text-primary)] mb-1">{fieldName}{isRequired && <span className="text-[var(--error)]"> *</span>}</label>
          <div className="border-2 border-[var(--border-primary)] rounded h-20 bg-[var(--background-subtle)]"></div>
        </div>
      );
    }
    if (fieldType === 'rating') {
      return (
        <div key={idx} className="mb-4">
          <label className="block text-sm font-semibold text-[var(--text-primary)] mb-2">{fieldName}{isRequired && <span className="text-[var(--error)]"> *</span>}</label>
          <div className="flex gap-2">
            {[1,2,3,4,5].map(n => <div key={n} className="w-10 h-10 border-2 border-gray-400 rounded flex items-center justify-center font-bold text-[var(--text-secondary)]">{n}</div>)}
          </div>
        </div>
      );
    }
    if (fieldType === 'select' && field.options) {
      return (
        <div key={idx} className="mb-4">
          <label className="block text-sm font-semibold text-[var(--text-primary)] mb-2">{fieldName}{isRequired && <span className="text-[var(--error)]"> *</span>}</label>
          <div className="flex flex-wrap gap-3">
            {field.options.map((opt) => (
              <div key={opt} className="flex items-center gap-2"><div className="w-4 h-4 border-2 border-gray-400 rounded-sm"></div><span className="text-sm text-[var(--text-secondary)]">{opt}</span></div>
            ))}
          </div>
        </div>
      );
    }
    return (
      <div key={idx} className="mb-4">
        <label className="block text-sm font-semibold text-[var(--text-primary)] mb-1">{fieldName}{isRequired && <span className="text-[var(--error)]"> *</span>}</label>
        <div className="border-b-2 border-[var(--border-primary)] h-8"></div>
      </div>
    );
  };

  const currentBuiltInForms = builtInForms[activeCategory] || [];
  const currentCustomForms = customTemplates.filter(t => t.category === activeCategory);
  const currentCategory = formCategories.find(c => c.id === activeCategory);

  if (loading) {
    return <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--primary)]"></div></div>;
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold font-['Cormorant_Garamond']">Forms Management</h1>
          <p className="text-[var(--text-muted)] mt-1">View, fill, sign, and manage all business forms</p>
        </div>
        <Button onClick={() => setCreateModal(true)} className="btn-primary" data-testid="create-template-btn">
          <Plus className="w-4 h-4 mr-2" />Create Custom Form
        </Button>
      </div>

      {/* Main Tabs: Templates vs Submissions */}
      <div className="flex gap-2 mb-6">
        <button onClick={() => setMainTab('templates')}
          className={`flex items-center gap-2 px-5 py-3 rounded-lg font-medium transition-all ${mainTab === 'templates' ? 'bg-[var(--primary)] text-white shadow-md' : 'bg-[var(--background-subtle)] text-[var(--text-secondary)] hover:bg-[var(--background-subtle)]'}`}>
          <List className="w-5 h-5" />Form Templates
        </button>
        <button onClick={() => setMainTab('submissions')}
          className={`flex items-center gap-2 px-5 py-3 rounded-lg font-medium transition-all ${mainTab === 'submissions' ? 'bg-[var(--primary)] text-white shadow-md' : 'bg-[var(--background-subtle)] text-[var(--text-secondary)] hover:bg-[var(--background-subtle)]'}`}>
          <Inbox className="w-5 h-5" />Submitted Forms
          {submissions.length > 0 && <span className="bg-[var(--background-paper)]/20 px-2 py-0.5 rounded-full text-sm">{submissions.length}</span>}
        </button>
      </div>

      {mainTab === 'templates' && (
        <>
          {/* Category Tabs */}
          <div className="flex flex-wrap gap-2 mb-6 p-2 bg-[var(--background-subtle)] rounded-xl">
            {formCategories.map(cat => {
              const Icon = getIconComponent(cat.icon);
              const count = (builtInForms[cat.id]?.length || 0) + customTemplates.filter(t => t.category === cat.id).length;
              return (
                <button key={cat.id} onClick={() => setActiveCategory(cat.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeCategory === cat.id ? 'bg-[var(--background-paper)] shadow-sm text-[var(--primary)] border border-[var(--primary)]/20' : 'text-[var(--text-secondary)] hover:bg-[var(--background-paper)]/50'}`}>
                  <Icon className="w-4 h-4" />{cat.name}
                  <span className={`px-2 py-0.5 text-xs rounded-full ${activeCategory === cat.id ? 'bg-[var(--primary)]/10' : 'bg-[var(--background-subtle)]'}`}>{count}</span>
                </button>
              );
            })}
          </div>

          {currentCategory && (
            <div className="mb-6 p-4 bg-gradient-to-r from-[var(--primary)]/5 to-transparent rounded-xl border-l-4 border-[var(--primary)]">
              <h2 className="font-semibold text-lg">{currentCategory.name}</h2>
            </div>
          )}

          {/* Forms Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {currentBuiltInForms.map(template => {
              const Icon = getIconComponent(template.icon);
              return (
                <div key={template.id} className="card p-4 border-l-4 border-[var(--primary)] hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-[var(--primary)]/10 rounded-lg"><Icon className="w-5 h-5 text-[var(--primary)]" /></div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold truncate">{template.name}</h4>
                      <p className="text-sm text-[var(--text-muted)] mt-1 line-clamp-2">{template.description}</p>
                      <p className="text-xs text-gray-400 mt-2">{template.fields?.length || 0} fields</p>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button size="sm" variant="outline" onClick={() => setViewModal(template)} className="flex-1">
                      <Eye className="w-4 h-4 mr-1" />Preview
                    </Button>
                    <Button size="sm" onClick={() => { setFillModal(template); setFormValues({}); setSignatures({}); }} className="flex-1 bg-[var(--success)] hover:bg-[var(--success)]/80 text-white">
                      <PenTool className="w-4 h-4 mr-1" />Fill & Sign
                    </Button>
                  </div>
                </div>
              );
            })}
            {currentCustomForms.map(template => {
              const Icon = getIconComponent(template.icon);
              return (
                <div key={template.id} className="card p-4 border-l-4 border-purple-500 hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-[#F4C2C2]/15 rounded-lg"><Icon className="w-5 h-5 text-[#F4C2C2]" /></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold truncate">{template.name}</h4>
                        <span className="text-xs bg-[#F4C2C2]/15 text-[#F4C2C2] px-2 py-0.5 rounded">Custom</span>
                      </div>
                      <p className="text-sm text-[var(--text-muted)] mt-1 line-clamp-2">{template.description}</p>
                      <p className="text-xs text-gray-400 mt-2">{template.fields?.length || 0} fields</p>
                    </div>
                    <button 
                      onClick={() => handleDeleteTemplate(template.id)} 
                      className="p-2 bg-[var(--error)]/10 hover:bg-[var(--error)]/20 text-[var(--error)] rounded-lg transition-colors"
                      data-testid={`delete-template-${template.id}`}
                      title="Delete template"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button size="sm" variant="outline" onClick={() => setViewModal(template)} className="flex-1">
                      <Eye className="w-4 h-4 mr-1" />Preview
                    </Button>
                    <Button size="sm" onClick={() => { setFillModal(template); setFormValues({}); setSignatures({}); }} className="flex-1 bg-[var(--success)] hover:bg-[var(--success)]/80 text-white">
                      <PenTool className="w-4 h-4 mr-1" />Fill & Sign
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {mainTab === 'submissions' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Submitted Forms ({submissions.length})</h2>
          </div>
          
          {submissions.length === 0 ? (
            <div className="text-center py-12 bg-[var(--background-subtle)] rounded-xl">
              <Inbox className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-[var(--text-muted)]">No forms have been submitted yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {submissions.map(sub => (
                <div key={sub.id} className="card p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 cursor-pointer" onClick={() => setViewSubmissionModal(sub)}>
                      <div className={`p-2 rounded-lg ${sub.status === 'submitted' ? 'bg-[var(--warning)]/15' : sub.status === 'approved' ? 'bg-[var(--success)]/15' : 'bg-[var(--background-subtle)]'}`}>
                        {sub.status === 'submitted' ? <Clock className="w-5 h-5 text-[var(--warning)]" /> : 
                         sub.status === 'approved' ? <CheckCircle2 className="w-5 h-5 text-[var(--success)]" /> :
                         <XCircle className="w-5 h-5 text-[var(--text-secondary)]" />}
                      </div>
                      <div>
                        <h4 className="font-semibold">{sub.template_name}</h4>
                        <p className="text-sm text-[var(--text-muted)]">By {sub.submitted_by_name} • {new Date(sub.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        sub.status === 'submitted' ? 'bg-[var(--warning)]/15 text-yellow-700' :
                        sub.status === 'approved' ? 'bg-[var(--success)]/15 text-green-700' : 'bg-[var(--background-subtle)] text-[var(--text-primary)]'
                      }`}>{sub.status}</span>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadSubmissionPDF(sub);
                        }}
                        className="gap-1 text-[var(--primary)] border-[var(--primary)]/30 hover:bg-[var(--primary)]/10"
                        data-testid={`download-submission-pdf-${sub.id}`}
                      >
                        <Download className="w-4 h-4" />
                        PDF
                      </Button>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSubmission(sub.id);
                        }}
                        className="p-1.5 text-[var(--error)] hover:bg-[var(--error)]/10 rounded"
                        data-testid={`delete-submission-${sub.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      <Eye className="w-5 h-5 text-gray-400 cursor-pointer" onClick={() => setViewSubmissionModal(sub)} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* View/Print Modal */}
      <AnimatePresence>
        {viewModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setViewModal(null)}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} className="bg-[var(--background-paper)] rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="p-4 border-b sticky top-0 bg-[var(--background-paper)] z-10 flex items-center justify-between">
                <span className="font-semibold">Form Preview - Print View</span>
                <div className="flex gap-2">
                  <Button onClick={handlePrint} className="bg-[var(--success)] hover:bg-[var(--success)]/80 text-white"><Printer className="w-4 h-4 mr-2" />Print Form</Button>
                  <button onClick={() => setViewModal(null)} className="p-2 hover:bg-[var(--background-subtle)] rounded"><X className="w-5 h-5" /></button>
                </div>
              </div>
              <div className="p-8" ref={printRef}>
                <div className="text-center border-b-4 border-double border-[#1a365d] pb-4 mb-6">
                  <h1 className="text-3xl font-bold text-[#1a365d] tracking-wider">NC HELP HUB</h1>
                  <p className="text-sm text-[var(--text-muted)] mt-1">Rides, Courier, Laundry & Errands Services</p>
                  <p className="text-xs text-gray-400 mt-2">Tarboro, NC 27886 | nchelphub@outlook.com</p>
                </div>
                <p className="text-right text-xs text-gray-400 mb-4">Form ID: {viewModal.id?.toUpperCase()} | Rev. 2025</p>
                <div className="bg-[var(--background-subtle)] border-2 border-[#1a365d] p-4 text-center mb-4"><h2 className="text-xl font-bold text-[#1a365d]">{viewModal.name}</h2></div>
                <p className="text-center text-sm text-[var(--text-muted)] italic mb-6">{viewModal.description}</p>
                <p className="text-xs text-[var(--text-muted)] mb-6">Fields marked with <span className="text-[var(--error)]">*</span> are required</p>
                <div className="space-y-2">{(viewModal.fields || []).map((f, i) => renderPreviewField(f, i))}</div>
                <div className="mt-10 pt-4 border-t border-[var(--border-primary)] text-center">
                  <p className="text-xs text-[var(--text-muted)]">NC Help Hub | Non-Medical Services Only</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Fill & Sign Modal */}
      <AnimatePresence>
        {fillModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setFillModal(null)}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} className="bg-[var(--background-paper)] rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="p-4 border-b sticky top-0 bg-[var(--background-paper)] z-10 flex items-center justify-between">
                <div>
                  <h2 className="font-bold text-lg">{fillModal.name}</h2>
                  <p className="text-sm text-[var(--text-muted)]">Fill out and sign this form electronically</p>
                </div>
                <button onClick={() => setFillModal(null)} className="p-2 hover:bg-[var(--background-subtle)] rounded"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-6">
                <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg p-4 mb-6">
                  <p className="text-sm text-blue-800"><strong>NC Help Hub</strong> - {fillModal.description}</p>
                </div>
                {(fillModal.fields || []).map((f, i) => renderFillableField(f, i))}
                <div className="flex gap-3 mt-6 pt-4 border-t">
                  <Button variant="outline" onClick={() => setFillModal(null)} className="flex-1">Cancel</Button>
                  <Button onClick={handleSubmitForm} disabled={saving} className="flex-1 btn-primary">
                    <Send className="w-4 h-4 mr-2" />{saving ? 'Submitting...' : 'Submit Form'}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* View Submission Modal */}
      <AnimatePresence>
        {viewSubmissionModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setViewSubmissionModal(null)}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} className="bg-[var(--background-paper)] rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="p-4 border-b sticky top-0 bg-[var(--background-paper)] z-10 flex items-center justify-between">
                <div>
                  <h2 className="font-bold text-lg">{viewSubmissionModal.template_name}</h2>
                  <p className="text-sm text-[var(--text-muted)]">Submitted by {viewSubmissionModal.submitted_by_name}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    size="sm" 
                    onClick={() => handleDownloadSubmissionPDF(viewSubmissionModal)}
                    className="btn-primary gap-1"
                    data-testid="modal-download-pdf"
                  >
                    <Download className="w-4 h-4" />
                    Download PDF
                  </Button>
                  <button onClick={() => setViewSubmissionModal(null)} className="p-2 hover:bg-[var(--background-subtle)] rounded"><X className="w-5 h-5" /></button>
                </div>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-[var(--background-subtle)] p-3 rounded-lg">
                    <p className="text-xs text-[var(--text-muted)]">Submitted</p>
                    <p className="font-medium">{new Date(viewSubmissionModal.created_at).toLocaleString()}</p>
                  </div>
                  <div className="bg-[var(--background-subtle)] p-3 rounded-lg">
                    <p className="text-xs text-[var(--text-muted)]">Status</p>
                    <p className={`font-medium ${viewSubmissionModal.status === 'approved' ? 'text-[var(--success)]' : viewSubmissionModal.status === 'submitted' ? 'text-[var(--warning)]' : 'text-[var(--text-secondary)]'}`}>
                      {viewSubmissionModal.status.charAt(0).toUpperCase() + viewSubmissionModal.status.slice(1)}
                    </p>
                  </div>
                </div>
                
                <h3 className="font-semibold mb-3">Form Data</h3>
                <div className="space-y-3 bg-[var(--background-subtle)] p-4 rounded-lg">
                  {Object.entries(viewSubmissionModal.form_fields || {}).map(([key, value]) => (
                    <div key={key} className="flex justify-between border-b border-[var(--border-primary)] pb-2">
                      <span className="text-[var(--text-secondary)] capitalize">{key.replace(/_/g, ' ')}</span>
                      <span className="font-medium">{typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value || '-'}</span>
                    </div>
                  ))}
                </div>
                
                {viewSubmissionModal.signatures && Object.keys(viewSubmissionModal.signatures).length > 0 && (
                  <>
                    <h3 className="font-semibold mt-6 mb-3">Signatures</h3>
                    <div className="space-y-4">
                      {Object.entries(viewSubmissionModal.signatures).map(([key, value]) => (
                        <div key={key} className="bg-[var(--background-subtle)] p-3 rounded-lg">
                          <p className="text-sm text-[var(--text-muted)] mb-2 capitalize">{key.replace(/_signature/g, '').replace(/_/g, ' ')}</p>
                          {value && <img src={value} alt="Signature" className="max-h-20 border rounded" />}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create Template Modal */}
      <AnimatePresence>
        {createModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setCreateModal(false)}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} className="bg-[var(--background-paper)] rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="p-6 border-b sticky top-0 bg-[var(--background-paper)] z-10 flex items-center justify-between">
                <h2 className="text-xl font-bold">Create New Form Template</h2>
                <button onClick={() => setCreateModal(false)} className="text-gray-400 hover:text-[var(--text-secondary)]"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Form Name *</label>
                  <Input value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="e.g., Pet Care Waiver" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <Textarea value={formData.description} onChange={e => setFormData(p => ({ ...p, description: e.target.value }))} placeholder="Describe this form..." rows={2} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Category</label>
                  <select value={formData.category} onChange={e => setFormData(p => ({ ...p, category: e.target.value }))} className="w-full px-3 py-2 border border-[var(--border-primary)] rounded-lg bg-[var(--background-subtle)] text-[var(--text-primary)]">
                    {formCategories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                  </select>
                </div>
                <div>
                  <h3 className="font-semibold mb-3">Form Fields ({formData.fields.length})</h3>
                  {formData.fields.length > 0 && (
                    <div className="space-y-2 mb-4">
                      {formData.fields.map((field) => (
                        <div key={field.id} className="flex items-center gap-3 p-3 bg-[var(--background-subtle)] rounded-lg">
                          <GripVertical className="w-4 h-4 text-gray-400" />
                          <span className="flex-1 font-medium">{field.name}</span>
                          <span className="text-xs bg-[var(--background-subtle)] text-[var(--text-secondary)] px-2 py-0.5 rounded capitalize">{field.type}</span>
                          {field.required && <span className="text-xs bg-[var(--error)]/15 text-[var(--error)] px-2 py-0.5 rounded">Required</span>}
                          <button onClick={() => removeField(field.id)} className="text-[var(--error)] hover:text-red-700"><X className="w-4 h-4" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="p-4 bg-[#D4AF37]/10 rounded-lg border border-[#D4AF37]/30">
                    <h4 className="text-sm font-medium mb-3 text-blue-800">Add Field</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <Input value={newField.name} onChange={e => setNewField(p => ({ ...p, name: e.target.value }))} placeholder="Field name" />
                      <select value={newField.type} onChange={e => setNewField(p => ({ ...p, type: e.target.value }))} className="px-3 py-2 border border-[var(--border-primary)] rounded-lg bg-[var(--background-subtle)] text-[var(--text-primary)]">
                        <option value="text">Text</option><option value="textarea">Long Text</option><option value="number">Number</option>
                        <option value="date">Date</option><option value="time">Time</option><option value="phone">Phone</option>
                        <option value="email">Email</option><option value="address">Address</option><option value="checkbox">Checkbox</option>
                        <option value="select">Dropdown</option><option value="signature">Signature</option><option value="rating">Rating (1-5)</option>
                      </select>
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={newField.required} onChange={e => setNewField(p => ({ ...p, required: e.target.checked }))} className="rounded" />Required
                      </label>
                      <Button size="sm" onClick={addField}><Plus className="w-4 h-4 mr-1" />Add</Button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-6 border-t flex justify-end gap-3">
                <Button variant="outline" onClick={() => setCreateModal(false)}>Cancel</Button>
                <Button onClick={handleCreateTemplate} disabled={saving} className="btn-primary">
                  <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Create Template'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default FormTemplatesTab;
